import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import AdminLayout from '@/components/Layout';
import { Zap, Plus, Trash2, Pencil, X } from 'lucide-react';

const CODEC_OPTIONS = ['H.264', 'H.265', 'AV1', 'VP9'];
const RESOLUTION_OPTIONS = ['4K', '1080p', '720p', '480p', '360p'];

const EMPTY = { name: '', codec: 'H.264', bitrate: 3000, resolution: '1080p', is_active: true };

export default function Transcoding() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null); // null=create, otherwise = profile
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    try {
      const res = await api.get('/transcoding');
      setProfiles(res.data);
    } catch (err) {
      console.error('Profil yükleme hatası:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      codec: p.codec,
      bitrate: p.bitrate,
      resolution: p.resolution,
      is_active: p.is_active,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, bitrate: parseInt(form.bitrate) || 0 };
    try {
      if (editing) {
        await api.patch(`/transcoding/${editing._id}`, payload);
      } else {
        await api.post('/transcoding', payload);
      }
      setShowModal(false);
      setForm(EMPTY);
      setEditing(null);
      load();
    } catch (err) {
      alert(editing ? 'Profil güncellenemedi.' : 'Profil eklenemedi.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu transcode profilini silmek istediğinize emin misiniz?')) return;
    try {
      await api.delete(`/transcoding/${id}`);
      setProfiles(profiles.filter(p => p._id !== id));
    } catch (err) {
      alert('Silinemedi.');
    }
  };

  const codecBadge = (codec) => {
    const tone = codec === 'H.265' ? 'text-emerald-400 border-emerald-700/50' :
                 codec === 'AV1'   ? 'text-purple-400 border-purple-700/50' :
                 codec === 'VP9'   ? 'text-blue-400 border-blue-700/50' :
                                     'text-amber-400 border-amber-700/50';
    return tone;
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-white flex items-center gap-3" data-testid="transcoding-page-title">
            <Zap className="text-amber-500" /> Transcode Profilleri
          </h1>
          <button
            onClick={openCreate}
            className="bg-amber-500 hover:bg-amber-600 text-black px-4 py-2 rounded-lg font-bold flex items-center gap-2 transition-transform active:scale-95"
            data-testid="transcoding-add-btn"
          >
            <Plus size={20} /> Yeni Profil
          </button>
        </div>

        <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden shadow-2xl">
          <table className="w-full text-left text-white" data-testid="transcoding-table">
            <thead className="bg-slate-950 text-gray-400 text-xs uppercase font-bold tracking-wider">
              <tr>
                <th className="p-4 font-semibold text-white">Profil Adı</th>
                <th className="p-4 font-semibold text-white">Codec</th>
                <th className="p-4 font-semibold text-white text-right">Bitrate</th>
                <th className="p-4 font-semibold text-white">Çözünürlük</th>
                <th className="p-4 font-semibold text-white">Durum</th>
                <th className="p-4 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {loading ? (
                <tr><td colSpan="6" className="p-12 text-center text-gray-500 italic">Yükleniyor…</td></tr>
              ) : profiles.length === 0 ? (
                <tr>
                  <td colSpan="6" className="p-12 text-center text-gray-500 italic" data-testid="transcoding-empty">
                    Henüz profil yok. "Yeni Profil" butonunu kullanın.
                  </td>
                </tr>
              ) : (
                profiles.map(p => (
                  <tr key={p._id} className="hover:bg-slate-800/30 transition-colors group" data-testid={`profile-row-${p._id}`}>
                    <td className="p-4 font-bold text-white">{p.name}</td>
                    <td className="p-4">
                      <span className={`text-[10px] font-mono px-2 py-1 bg-slate-800 rounded border uppercase tracking-wider ${codecBadge(p.codec)}`}>
                        {p.codec}
                      </span>
                    </td>
                    <td className="p-4 text-right font-mono text-sm text-gray-300">
                      {p.bitrate?.toLocaleString('tr-TR')} <span className="text-gray-500 text-xs">kbps</span>
                    </td>
                    <td className="p-4">
                      <span className="text-xs px-2.5 py-1 bg-slate-800 text-slate-300 rounded-full border border-slate-700 font-medium">
                        {p.resolution}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={`flex items-center gap-1.5 text-[10px] font-bold uppercase ${p.is_active ? 'text-green-500' : 'text-gray-500'}`}>
                        <div className={`w-1.5 h-1.5 rounded-full ${p.is_active ? 'bg-green-500' : 'bg-gray-500'}`} />
                        {p.is_active ? 'Aktif' : 'Pasif'}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => openEdit(p)}
                          className="text-gray-500 hover:text-amber-500 hover:bg-amber-500/10 p-2 rounded-lg transition-all"
                          data-testid={`profile-edit-btn-${p._id}`}
                          title="Düzenle"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(p._id)}
                          className="text-gray-500 hover:text-red-500 hover:bg-red-500/10 p-2 rounded-lg transition-all"
                          data-testid={`profile-delete-btn-${p._id}`}
                          title="Sil"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-[100] p-4" data-testid="transcoding-modal">
            <div className="bg-slate-900 border border-slate-800 w-full max-w-xl rounded-2xl p-8 shadow-2xl">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-white flex items-center gap-2">
                  <Zap className="text-amber-500" /> {editing ? 'Profili Düzenle' : 'Yeni Transcode Profili'}
                </h3>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white" data-testid="transcoding-modal-close">
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1 md:col-span-2">
                  <label className="text-xs font-bold text-gray-500 uppercase ml-1">Profil Adı</label>
                  <input
                    required
                    placeholder="Örn: HD-Premium-H265"
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-white focus:border-amber-500 outline-none transition-all"
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    data-testid="profile-name-input"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase ml-1">Codec</label>
                  <select
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-white focus:border-amber-500 outline-none appearance-none cursor-pointer"
                    value={form.codec}
                    onChange={e => setForm({ ...form, codec: e.target.value })}
                    data-testid="profile-codec-select"
                  >
                    {CODEC_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase ml-1">Çözünürlük</label>
                  <select
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-white focus:border-amber-500 outline-none appearance-none cursor-pointer"
                    value={form.resolution}
                    onChange={e => setForm({ ...form, resolution: e.target.value })}
                    data-testid="profile-resolution-select"
                  >
                    {RESOLUTION_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div className="space-y-1 md:col-span-2">
                  <label className="text-xs font-bold text-gray-500 uppercase ml-1">Bitrate (kbps)</label>
                  <input
                    required
                    type="number"
                    min="100"
                    max="50000"
                    placeholder="3000"
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-white focus:border-amber-500 outline-none transition-all font-mono"
                    value={form.bitrate}
                    onChange={e => setForm({ ...form, bitrate: e.target.value })}
                    data-testid="profile-bitrate-input"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="flex items-center gap-3 cursor-pointer p-3 bg-slate-950 border border-slate-800 rounded-xl hover:border-amber-500/40 transition">
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={e => setForm({ ...form, is_active: e.target.checked })}
                      className="w-4 h-4 accent-amber-500"
                      data-testid="profile-active-checkbox"
                    />
                    <span className="text-sm text-white">Profil aktif (kanallara atanabilir)</span>
                  </label>
                </div>
                <div className="flex gap-4 pt-4 md:col-span-2">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 bg-slate-800 text-white font-bold py-4 rounded-xl hover:bg-slate-700 transition-colors"
                    data-testid="profile-cancel-btn"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-amber-500 text-black font-bold py-4 rounded-xl hover:bg-amber-600 transition-colors shadow-lg shadow-amber-500/20"
                    data-testid="profile-save-btn"
                  >
                    {editing ? 'Güncelle' : 'Kaydet'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
